import React, { useState, useEffect, useContext, useRef } from 'react';
import { createFragmentContainer, graphql } from 'react-relay';
import PropTypes from 'prop-types';
import { Redirect } from 'react-router-dom';

import EventPendingView from '../components/content/event/EventPendingView';
import EventOnlineView from '../components/content/event/EventOnlineView';
import PaymentMethodView from '../components/content/PaymentMethodView';
import PastEventInformation from '../components/content/event/PastEventInformation';
import Pricelist from '../components/content/event/Pricelist';

import { HelpdeskWidgetContext } from '../components/content/HelpdeskWidget';
import { UserContext } from '../components/context/UserContext';
import { FACTOR_EMAIL, PENDING } from '../utils/variables';
import { customScrollToComponent } from '../utils/common';
import EventCommonPart from '../components/content/event/EventCommonPart';

const EventPage = (props) => {
  const { event } = props.viewer;

  const priceOffer = useRef(null);

  const eventOnline = event.state !== PENDING;

  let initialSelectedTab = 1;

  let pastEventNotificationHorizon = new Date();
  pastEventNotificationHorizon.setDate(pastEventNotificationHorizon.getDate() - 90);
  let currentEventDate = new Date(event.beginsAt);

  const [showPastEventNotification, setShowPastEventNotification] = useState(
    currentEventDate < pastEventNotificationHorizon
  );
  const [extendedSearchSelectedTab, setExtendedSearchSelectedTab] =
    useState(initialSelectedTab);
  const [extendedSearchExpand, setExtendedSearchExpand] = useState(false);

  const [hlpDskState, hlpDskDispatch] = useContext(HelpdeskWidgetContext);
  const { onMarathonFotoChange } = useContext(UserContext);
  const [bestofRef, setBestofRef] = useState();
  const [searchformRef, setSearchFormRef] = useState();
  const [showHowToBuyHint, setShowHowToBuyHint] = useState(false);
  const [besOfAvailable, setBestOfAvailable] = useState(false);

  useEffect(() => {
    hlpDskDispatch({ type: 'eventName', payload: event.name });
    hlpDskDispatch({ type: 'eventId', payload: event.sgId });
    hlpDskDispatch({ type: 'status', payload: event.state });
  }, [event, hlpDskDispatch]);

  useEffect(() => {
    onMarathonFotoChange(event.marathonFoto);
  }, []);

  const handleClosePastEventNotification = () => {
    setShowPastEventNotification(false);
  };

  const handleExtendedSearchTabChanged = (event, value) => {
    setExtendedSearchExpand(true);
    setExtendedSearchSelectedTab(value);
  };

  const handleExtendedSearchExpand = () => {
    setExtendedSearchExpand(!extendedSearchExpand);
  };

  const scrollToOffer = () => {
    if (priceOffer.current) {
      customScrollToComponent({ currentRef: priceOffer.current });
    }
  };

  const scrollToBestof = () => {
    if (bestofRef) {
      customScrollToComponent({ currentRef: bestofRef, position: 'middle' });
    }
  };

  const scrollToSearchForm = () => {
    if (searchformRef) {
      const { current } = searchformRef;
      if (current) {
        customScrollToComponent({ currentRef: current });
      } else {
        customScrollToComponent({ currentRef: searchformRef });
      }
    }
  };

  const updateHowToBuyTooltipVisibility = (shouldShow) => {
    setShowHowToBuyHint(shouldShow);
  };

  // In case a user navigates directly over the url, it is necessary to check if the
  // event has subevents. If so, forward him to the appropriate page.
  if (event.subEvents.length > 0) {
    return <Redirect to={`/event/${event.sgId}/subevents`} />;
  }

  //support wish to redirect from this event
  if (event.sgId === 9417) {
    return <Redirect to={`/event/11693`} />;
  }

  return (
    <div className="container-960 container-page">
      <EventCommonPart
        event={event}
        expandBestOfOnInit={!eventOnline}
        liftUpBestofPosition={setBestofRef}
        onScrollToBestOf={scrollToBestof}
        onExtrasClick={priceOffer.current && scrollToOffer}
        sendBestOfAvailability={setBestOfAvailable}
      >
        {event.successorEvent && showPastEventNotification && (
          <PastEventInformation
            eventname={event.name}
            currentEventDate={event.beginsAt}
            successorEvent={event.successorEvent}
            onRequestClose={handleClosePastEventNotification}
          />
        )}
        {!eventOnline && (
          <EventPendingView pendingEvent={event} bestOfAvailable={besOfAvailable} />
        )}
        {eventOnline && (
          <EventOnlineView
            onlineEvent={event}
            extendedSearchSelectedTab={extendedSearchSelectedTab}
            extendedSearchExpand={extendedSearchExpand}
            hasGpsSearchTab={event.gps}
            hasDualSearchTab={event.searchDual}
            hasSmartSearchTab={false}
            hasCategorySearchTab={event.searchCollections}
            hasExtendedSearch={
              !event.secondFactorType ||
              (event.secondFactorType.type == FACTOR_EMAIL && event.participantUser)
            }
            onExtendedSearchTabChange={handleExtendedSearchTabChanged}
            onExtendedSearchExpand={handleExtendedSearchExpand}
            handleScrollToOffer={scrollToOffer}
            onScrollToSearchForm={scrollToSearchForm}
            shouldShowAdditionalOffer={true}
            setFormRef={setSearchFormRef}
            showHowToBuyHint={showHowToBuyHint}
            onShowHowToBuyHint={updateHowToBuyTooltipVisibility}
            eventSecondFactorType={event.secondFactorType && event.secondFactorType.type}
            // user is never authenticated at this point
            isParticipant={false}
          />
        )}
      </EventCommonPart>
      <div ref={priceOffer}>
        <Pricelist offer={event} onScrollToSearchForm={scrollToSearchForm} />
      </div>
      <PaymentMethodView />
    </div>
  );
};

EventPage.propTypes = {
  match: PropTypes.object,
  viewer: PropTypes.object,
};

export { EventPage };

export default createFragmentContainer(EventPage, {
  viewer: graphql`
    fragment EventPage_viewer on Viewer {
      event(id: $eventId, language: $language) {
        id
        sgId
        beginsAt
        gps
        name
        secondFactorType {
          type
        }
        participantUser
        state
        searchDual
        searchCollections
        subEvents {
          id
        }
        successorEvent {
          ...PastEventInformation_successorEvent
        }
        voucherCampaigns {
          id
        }
        products {
          type
        }
        marathonFoto
        ...EventPendingView_pendingEvent
        ...EventOnlineView_onlineEvent
        ...Pricelist_offer
        ...EventBar_event
        ...EventCommonPart_event
      }
    }
  `,
});
